/**
 * GA4 Ecommerce Analytics — fires standard gtag events.
 * Set REACT_APP_GA4_ID in .env to activate.
 */

const gtag = (...args) => {
  if (typeof window.gtag === 'function') window.gtag(...args);
};

/** Fires on every route change (called from App.js) */
export const trackPageView = (path) => {
  gtag('event', 'page_view', { page_path: path });
};

/** Product detail page viewed */
export const trackViewItem = (product) => {
  gtag('event', 'view_item', {
    currency: 'GBP',
    value: product.price,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_brand: product.brand || '',
      item_category: product.category || '',
      price: product.price,
    }],
  });
};

/** Product list / shop page viewed */
export const trackViewItemList = (products, listName = 'Shop') => {
  gtag('event', 'view_item_list', {
    item_list_name: listName,
    items: products.slice(0, 20).map((p, i) => ({
      item_id: p.id || p._id,
      item_name: p.name,
      item_brand: p.brand || '',
      item_category: p.category || '',
      price: p.price,
      index: i,
    })),
  });
};

/** Add to cart */
export const trackAddToCart = (product, quantity = 1, size = '', color = '') => {
  gtag('event', 'add_to_cart', {
    currency: 'GBP',
    value: product.price * quantity,
    items: [{
      item_id: product.id,
      item_name: product.name,
      item_brand: product.brand || '',
      item_category: product.category || '',
      item_variant: [size, color].filter(Boolean).join(' / '),
      price: product.price,
      quantity,
    }],
  });
};

/** Begin checkout */
export const trackBeginCheckout = (cartItems, total) => {
  gtag('event', 'begin_checkout', {
    currency: 'GBP',
    value: total,
    items: cartItems.map((item) => ({
      item_id: item.product_id,
      item_name: item.product_name || item.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

/** Purchase complete */
export const trackPurchase = (order) => {
  gtag('event', 'purchase', {
    transaction_id: order.id || order._id,
    currency: 'GBP',
    value: order.final_total || order.total,
    shipping: order.shipping_cost || 0,
    items: (order.items || []).map((item) => ({
      item_id: item.product_id,
      item_name: item.product_name || '',
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

/** Search performed */
export const trackSearch = (searchTerm) => {
  gtag('event', 'search', { search_term: searchTerm });
};

/** Wishlist add */
export const trackAddToWishlist = (product) => {
  gtag('event', 'add_to_wishlist', {
    currency: 'GBP',
    value: product.price,
    items: [{
      item_id: product.id || product._id,
      item_name: product.name,
      item_brand: product.brand || '',
      item_category: product.category || '',
      price: product.price,
    }],
  });
};

/** Wishlist remove */
export const trackRemoveFromWishlist = (productId) => {
  gtag('event', 'remove_from_wishlist', { item_id: productId });
};

/** Coupon applied */
export const trackApplyCoupon = (code, discount) => {
  gtag('event', 'select_promotion', {
    promotion_id: code,
    promotion_name: code,
    creative_name: `${discount}% off`,
  });
};
